import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Target, Rocket, Lock } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FloatingTrialBubble from '@/components/FloatingTrialBubble';

// Score projection lookup tables
const RW_SCORE_TABLE = [
  { correct: 0, min: 200, max: 320 },
  { correct: 1, min: 320, max: 440 },
  { correct: 2, min: 440, max: 540 },
  { correct: 3, min: 540, max: 640 },
  { correct: 4, min: 640, max: 740 },
  { correct: 5, min: 740, max: 800 },
];

const MATH_SCORE_TABLE = [
  { correct: 0, min: 200, max: 320 },
  { correct: 1, min: 320, max: 450 },
  { correct: 2, min: 450, max: 560 },
  { correct: 3, min: 560, max: 670 },
  { correct: 4, min: 670, max: 760 },
  { correct: 5, min: 760, max: 800 },
];

const PERCENTILE_TABLE = [
  { minScore: 400, maxScore: 520, percentile: '1st–5th', minPct: 1, maxPct: 5 },
  { minScore: 520, maxScore: 650, percentile: '5th–15th', minPct: 5, maxPct: 15 },
  { minScore: 650, maxScore: 760, percentile: '15th–25th', minPct: 15, maxPct: 25 },
  { minScore: 760, maxScore: 880, percentile: '25th–40th', minPct: 25, maxPct: 40 },
  { minScore: 880, maxScore: 1010, percentile: '40th–55th', minPct: 40, maxPct: 55 },
  { minScore: 1010, maxScore: 1150, percentile: '55th–70th', minPct: 55, maxPct: 70 },
  { minScore: 1150, maxScore: 1280, percentile: '70th–85th', minPct: 70, maxPct: 85 },
  { minScore: 1280, maxScore: 1400, percentile: '85th–93rd', minPct: 85, maxPct: 93 },
  { minScore: 1400, maxScore: 1510, percentile: '93rd–97th', minPct: 93, maxPct: 97 },
  { minScore: 1510, maxScore: 1580, percentile: '97th–99th', minPct: 97, maxPct: 99 },
  { minScore: 1580, maxScore: 1600, percentile: '99th+', minPct: 99, maxPct: 100 },
];

const getImprovementMessage = (score) => {
  if (score <= 890) {
    return "Students in this range often improve by 150–300+ points with structured practice.";
  } else if (score <= 1290) {
    return "Students in this range often improve by 100–180 points with targeted practice.";
  } else {
    return "Students in this range often improve by 60–120 points by refining advanced concepts and timing strategy.";
  }
};

export default function QuickQuizResults() {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questionsWithCorrectAnswers, setQuestionsWithCorrectAnswers] = useState([]);
  const emailSentRef = React.useRef(false);
  
  // Auth modal state - simplified, now redirects to landing page
  const handleUnlockPlan = () => {
    // Navigate to landing page and scroll to pricing section
    navigate('/?signup=true#pricing');
  };

  useEffect(() => {
    document.title = 'Quiz Results - Practice4SAT';
    loadResults();
  }, []);

  useEffect(() => {
    if (results && questionsWithCorrectAnswers.length > 0 && !emailSentRef.current && !loading) {
      emailSentRef.current = true;
      sendCompletionEmail();
    }
  }, [results, questionsWithCorrectAnswers, loading]);

  const sendCompletionEmail = async () => {
    try {
      const score = calculateScore();
      const projected = getProjectedScores(score.rwCorrect, score.mathCorrect);
      const startTime = new Date(results.startTime || results.completedAt);
      const endTime = new Date(results.completedAt);
      const timeTakenSeconds = Math.max(0, Math.floor((endTime - startTime) / 1000)) || 0;
      
      await axios.post(`${API}/quick-quiz/complete`, {
        score: score.correct,
        total: score.total,
        rw_correct: score.rwCorrect,
        math_correct: score.mathCorrect,
        projected_score: projected.totalMidpoint,
        time_taken_seconds: timeTakenSeconds
      });
    } catch (error) {
      console.error('Error sending completion email:', error);
    }
  };

  const loadResults = async () => {
    try {
      const storedResults = localStorage.getItem('quickQuizResults');
      if (!storedResults) {
        navigate('/');
        return;
      }

      const parsedResults = JSON.parse(storedResults);
      setResults(parsedResults);

      const questionIds = parsedResults.questions.map(q => q.id);
      const response = await axios.post(`${API}/diagnostic/quiz/get-questions`, {
        question_ids: questionIds
      });

      setQuestionsWithCorrectAnswers(response.data.questions);
      setLoading(false);
    } catch (error) {
      console.error('Error loading results:', error);
      setLoading(false);
    }
  };

  const calculateScore = () => {
    if (!results || questionsWithCorrectAnswers.length === 0) {
      return { correct: 0, total: 0, rwCorrect: 0, mathCorrect: 0 };
    }

    let correct = 0;
    let rwCorrect = 0;
    let mathCorrect = 0;
    
    results.questions.forEach((q, idx) => {
      const userAnswer = results.answers[q.id];
      const questionData = questionsWithCorrectAnswers.find(qd => qd.id === q.id);
      
      if (questionData && userAnswer) {
        let isCorrect = false;
        if (questionData.question_type === 'multiple_choice') {
          isCorrect = userAnswer === questionData.correct_answer;
        } else {
          const normalizedUser = userAnswer.toString().trim().toLowerCase();
          const normalizedCorrect = questionData.correct_answer.toString().trim().toLowerCase();
          isCorrect = normalizedUser === normalizedCorrect;
        }
        
        if (isCorrect) {
          correct++;
          if (idx < 5) rwCorrect++;
          else mathCorrect++;
        }
      }
    });

    return { correct, total: results.questions.length, rwCorrect, mathCorrect };
  };

  const getIncorrectTopics = () => {
    if (!results || questionsWithCorrectAnswers.length === 0) {
      return [];
    }

    const difficultyOrder = { 'hard': 1, 'medium': 2, 'easy': 3 };
    const incorrectItems = [];
    
    results.questions.forEach((q, idx) => {
      const userAnswer = results.answers[q.id];
      const questionData = questionsWithCorrectAnswers.find(qd => qd.id === q.id);
      
      if (questionData) {
        let isCorrect = false;
        if (userAnswer) {
          if (questionData.question_type === 'multiple_choice') {
            isCorrect = userAnswer === questionData.correct_answer;
          } else {
            const normalizedUser = userAnswer.toString().trim().toLowerCase();
            const normalizedCorrect = questionData.correct_answer.toString().trim().toLowerCase();
            isCorrect = normalizedUser === normalizedCorrect;
          }
        }
        
        if (!isCorrect && questionData.topic) {
          const section = idx < 5 ? 'rw' : 'math';
          incorrectItems.push({
            topic: questionData.topic,
            difficulty: questionData.difficulty || 'medium',
            section
          });
        }
      }
    });

    // Sort by difficulty (hard first, then medium, then easy)
    incorrectItems.sort((a, b) => {
      return (difficultyOrder[a.difficulty] || 2) - (difficultyOrder[b.difficulty] || 2);
    });

    // Get unique topics, trying to balance between sections
    const seenTopics = new Set();
    const result = [];
    
    // First pass: try to get one from each section
    for (const item of incorrectItems) {
      if (!seenTopics.has(item.topic)) {
        seenTopics.add(item.topic);
        result.push(item.topic);
        if (result.length >= 3) break;
      }
    }

    return result;
  };

  const getProjectedScores = (rwCorrect, mathCorrect) => {
    const rw = RW_SCORE_TABLE[rwCorrect] || RW_SCORE_TABLE[0];
    const math = MATH_SCORE_TABLE[mathCorrect] || MATH_SCORE_TABLE[0];
    
    const totalMin = rw.min + math.min;
    const totalMax = rw.max + math.max;
    const totalMidpoint = Math.round((totalMin + totalMax) / 2);
    const rwMidpoint = Math.round((rw.min + rw.max) / 2);
    const mathMidpoint = Math.round((math.min + math.max) / 2);

    return {
      rw: { min: rw.min, max: rw.max, midpoint: rwMidpoint },
      math: { min: math.min, max: math.max, midpoint: mathMidpoint },
      totalMin,
      totalMax,
      totalMidpoint
    };
  };

  const getPercentileInfo = (score) => {
    for (const tier of PERCENTILE_TABLE) {
      if (score >= tier.minScore && score <= tier.maxScore) {
        return tier;
      }
    }
    return PERCENTILE_TABLE[0];
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '2rem' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Calculating Results...</div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>No results found</h2>
        <Button onClick={() => navigate('/')}>Return Home</Button>
      </div>
    );
  }

  const score = calculateScore();
  const projected = getProjectedScores(score.rwCorrect, score.mathCorrect);
  const percentileInfo = getPercentileInfo(projected.totalMidpoint);
  const incorrectTopics = getIncorrectTopics();
  const improvementMessage = getImprovementMessage(projected.totalMidpoint);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header variant="minimal" />
      <FloatingTrialBubble />

      <div style={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div 
          className="quiz-results-card"
          style={{ 
            background: 'white', 
            borderRadius: '16px', 
            padding: '1.5rem', 
            maxWidth: '650px', 
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}
        >
          {/* Header */}
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem', color: '#1a202c', textAlign: 'center' }}>
            Your Projected SAT Score
          </h1>

          {/* Combined Score Display - Total on left, Sections on right */}
          <div 
            className="quiz-results-score-grid"
            style={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '1rem'
            }}
          >
            {/* Total Score - Left side, full height */}
            <div 
              className="quiz-results-total-score"
              style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '1rem',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.15rem' }}>
                Projected SAT Score
              </div>
              <div className="quiz-results-total-number" style={{ fontSize: '2.5rem', fontWeight: '700', color: 'white', lineHeight: 1 }}>
                {projected.totalMidpoint}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', marginTop: '0.15rem' }}>
                Range: {projected.totalMin}–{projected.totalMax}
              </div>
            </div>

            {/* Section Scores - Right side, stacked */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Reading & Writing - Top half */}
              <div 
                className="quiz-results-section-score"
                style={{ 
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  padding: '0.5rem',
                  textAlign: 'center',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.9)' }}>
                  Reading & Writing
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', lineHeight: 1 }}>
                  {projected.rw.midpoint}
                </div>
                <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)' }}>
                  {projected.rw.min}–{projected.rw.max} ({score.rwCorrect}/5)
                </div>
              </div>

              {/* Math - Bottom half */}
              <div 
                className="quiz-results-section-score"
                style={{ 
                  background: 'linear-gradient(135deg, #0030b3 0%, #3b82f6 100%)',
                  padding: '0.5rem',
                  textAlign: 'center',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.9)' }}>
                  Math
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', lineHeight: 1 }}>
                  {projected.math.midpoint}
                </div>
                <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)' }}>
                  {projected.math.min}–{projected.math.max} ({score.mathCorrect}/5)
                </div>
              </div>
            </div>
          </div>

          {/* Percentile Info */}
          <div style={{ 
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '10px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <Target size={18} color="#16a34a" style={{ flexShrink: 0, marginTop: '0.15rem' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#166534', fontSize: '0.95rem', lineHeight: '1.5' }}>
                  You scored higher than {percentileInfo.minPct}–{percentileInfo.maxPct}% of test takers.
                </div>
                <div style={{ fontSize: '0.85rem', color: '#15803d', marginTop: '0.25rem', lineHeight: '1.5' }}>
                  {improvementMessage}
                </div>
              </div>
            </div>
          </div>

          {/* Focus Areas */}
          {incorrectTopics.length > 0 && (
            <div style={{ 
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', gap: '0.5rem', fontWeight: '600', color: '#1e40af', fontSize: '0.95rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                <Rocket size={18} color="#2563eb" style={{ flexShrink: 0, marginTop: '0.15rem' }} />
                <span style={{ flex: 1, lineHeight: '1.5' }}>Based on your results, focus on these areas to increase your score:</span>
              </div>
              <ul style={{ margin: 0, padding: 0, paddingLeft: '1.75rem', listStyle: 'none' }}>
                {incorrectTopics.map((topic, idx) => (
                  <li key={`topic-${topic}`} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem', alignItems: 'flex-start' }}>
                    <span style={{ color: '#2563eb', fontWeight: '600', flexShrink: 0, marginTop: '0.15rem' }}>✓</span>
                    <span style={{ flex: 1, color: '#1d4ed8', fontSize: '0.9rem', lineHeight: '1.5' }}>{topic}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Study Plan Includes */}
          <div style={{ 
            background: '#faf5ff',
            border: '1px solid #e9d5ff',
            borderRadius: '10px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem', fontWeight: '600', color: '#7c3aed', fontSize: '0.95rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
              <Lock size={18} color="#7c3aed" style={{ flexShrink: 0, marginTop: '0.15rem' }} />
              <span style={{ flex: 1, lineHeight: '1.5' }}>Your Full Study Plan Includes:</span>
            </div>
            <ul style={{ margin: 0, padding: 0, paddingLeft: '1.75rem', listStyle: 'none' }}>
              <li style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem', alignItems: 'flex-start' }}>
                <span style={{ color: '#7c3aed', fontWeight: '600', flexShrink: 0, marginTop: '0.15rem' }}>✓</span>
                <span style={{ flex: 1, color: '#6b21a8', fontSize: '0.9rem', lineHeight: '1.5' }}>Prioritized skill roadmap based on your performance</span>
              </li>
              <li style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem', alignItems: 'flex-start' }}>
                <span style={{ color: '#7c3aed', fontWeight: '600', flexShrink: 0, marginTop: '0.15rem' }}>✓</span>
                <span style={{ flex: 1, color: '#6b21a8', fontSize: '0.9rem', lineHeight: '1.5' }}>Adaptive practice aligned to your highest-impact areas</span>
              </li>
              <li style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem', alignItems: 'flex-start' }}>
                <span style={{ color: '#7c3aed', fontWeight: '600', flexShrink: 0, marginTop: '0.15rem' }}>✓</span>
                <span style={{ flex: 1, color: '#6b21a8', fontSize: '0.9rem', lineHeight: '1.5' }}>5–8 full-length SAT tests with calibrated scoring</span>
              </li>
              <li style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <span style={{ color: '#7c3aed', fontWeight: '600', flexShrink: 0, marginTop: '0.15rem' }}>✓</span>
                <span style={{ flex: 1, color: '#6b21a8', fontSize: '0.9rem', lineHeight: '1.5' }}>Progress tracking and measurable score improvement</span>
              </li>
            </ul>
          </div>

          {/* CTA Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <Button 
              onClick={handleUnlockPlan}
              style={{ 
                width: '100%', 
                padding: '1rem', 
                fontSize: '1.1rem',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                fontWeight: '600'
              }}
              data-testid="unlock-plan-btn"
            >
              Unlock My Personalized Study Plan
            </Button>
            <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#9ca3af', margin: '-0.25rem 0 0 0' }}>
              Free • No credit card needed
            </p>
            <Button 
              variant="outline"
              onClick={() => {
                localStorage.removeItem('quickQuizResults');
                navigate('/');
              }}
              style={{ width: '100%' }}
            >
              Return to Home
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
